import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { requireUid } from './auth';
import { storage } from './app';
import { pendant } from '../native/arrierePlan';

/**
 * Photos et sons, ranges dans Cloud Storage.
 *
 * Ils vivaient dans la Realtime Database, encodes en data URL. Le
 * stockage n'a jamais ete le probleme — quelques mega-octets sur un
 * gigaoctet gratuit — mais le telechargement : une image en base ne se
 * met pas en cache, donc chaque ouverture du fil la retelechargeait
 * entierement. Sur Storage elle devient une URL HTTPS, que WebKit garde
 * de lui-meme, et que la base ne transporte plus qu'en une centaine
 * d'octets.
 *
 * Ce module est le seul a connaitre Storage. Les ecrans ne voient que des
 * URL, sans savoir si elles sortent d'ici ou d'une ancienne data URL.
 */

/**
 * Ce qui rend le cache possible.
 *
 * Sans en-tete de cache, Storage repond `no-cache` et WebKit redemande le
 * fichier a chaque affichage : on aurait deplace les octets sans rien
 * gagner. Un an, et `immutable` parce que le nom d'un fichier ne resert
 * jamais — une photo modifiee est une nouvelle photo, sous une nouvelle
 * cle.
 */
const CACHE = 'public, max-age=31536000, immutable';

/*
  Extension deduite du type, pour que le fichier reste lisible tel quel.

  Les videos n'y figuraient pas : elles etaient rangees en `.bin`. Le
  type declare reste juste, donc la lecture n'en souffrait pas — mais un
  seau ou l'on ne distingue pas un film d'un fichier illisible est un
  seau qu'on ne saura pas fouiller le jour ou il faudra.
*/
const EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'audio/mp4': 'm4a',
  'audio/mpeg': 'mp3',
  'audio/webm': 'webm',
  'audio/ogg': 'ogg',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
};

/**
 * Televerse un media et rend son URL definitive.
 *
 * Le fichier est range sous l'identifiant de celui qui l'envoie : c'est
 * la seule chose que les regles de Storage savent verifier, faute de
 * pouvoir lire la base.
 */
export async function uploadMedia(blob: Blob): Promise<string> {
  const uid = requireUid();
  const type = blob.type || 'application/octet-stream';
  /*
    Le type porte parfois ses codecs — `video/webm;codecs=vp9`, et de meme
    pour les vocaux en opus. La table ne connait que la famille : on
    cherche donc sur ce qui precede le point-virgule, sinon tout ce qui
    est enregistre par le navigateur atterrit en `.bin`.
  */
  const extension = EXTENSIONS[type.split(';')[0].trim()] ?? 'bin';

  /*
    Un nom horodate puis tire au sort : unique sans aller-retour, et
    chronologique, ce qui rend une liste de fichiers lisible a l'oeil le
    jour ou il faudra fouiller le seau. Les chemins de Storage ne passent
    pas par `paths.ts` : ce fichier decrit la base, et il n'y a pas de
    branche `media` en base.
  */
  const name = `${Date.now().toString(36)}-${crypto.randomUUID().slice(0, 8)}.${extension}`;

  const target = ref(storage(), `media/${uid}/${name}`);
  /*
    Sous la protection du sursis : iOS suspend la vue web des qu'on
    quitte l'application, et l'envoi d'une video de quatre mega-octets se
    coupait alors net — le message ne partait jamais, sans que rien ne le
    dise. C'est ici, et non aux appelants, parce que tout media passe par
    cette ligne. Sans effet hors de l'application native.
  */
  await pendant(() => uploadBytes(target, blob, { contentType: type, cacheControl: CACHE }));
  return getDownloadURL(target);
}

/**
 * Une data URL rendue televersable.
 *
 * `fetch` sait decoder une data URL sans reseau : c'est la conversion la
 * plus courte, et la seule qui n'ait pas a manipuler du base64 a la main.
 */
export async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  return (await fetch(dataUrl)).blob();
}

/**
 * Televerse ce qui est encore une data URL, laisse passer le reste.
 *
 * Les deux formes cohabitent le temps que l'existant soit migre : une URL
 * HTTPS traverse cette fonction sans etre touchee.
 */
export async function uploadIfDataUrl(url: string): Promise<string> {
  if (!url.startsWith('data:')) return url;
  return uploadMedia(await dataUrlToBlob(url));
}
